/**
 * Currency Picker Modal
 * Opens a searchable bottom-sheet picker that lets the user select a currency.
 * Uses Intl.DisplayNames to resolve human-readable currency names.
 */

// Curated list of commonly-used currencies shown at the top
const POPULAR_CURRENCIES = [
    'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY',
    'INR', 'MXN', 'BRL', 'KRW', 'SGD', 'HKD', 'NOK', 'SEK',
    'DKK', 'NZD', 'ZAR', 'AED', 'PLN', 'THB', 'TRY', 'RUB',
    'MYR', 'IDR', 'PHP', 'VND', 'CZK', 'HUF', 'ILS', 'CLP',
    'COP', 'PEN', 'ARS', 'UAH', 'RON', 'SAR', 'QAR', 'KWD'
];

// Resolve display name for a currency code
function getCurrencyName(code, displayNames) {
    try {
        return displayNames.of(code) || code;
    } catch {
        return code;
    }
}

// Get the currency symbol (e.g., "$", "€")
function getCurrencySymbol(code) {
    try {
        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(0);
        // Extract just the symbol by removing digits
        return formatted.replace(/[\d,.\s]/g, '').trim();
    } catch {
        return code;
    }
}

/**
 * Opens a currency picker modal.
 * @param {string} currentCurrency - The currently selected currency code
 * @param {function(string): void} onSelect - Callback when user picks a currency
 */
export function openCurrencyPicker(currentCurrency, onSelect) {
    const displayNames = new Intl.DisplayNames(['en-US'], { type: 'currency' });

    // Build a full list: popular first, then all others alphabetically
    let allCodes;
    try {
        // Try to get supported currencies from Intl
        allCodes = Intl.supportedValuesOf('currency');
    } catch {
        // Fallback: use only popular list
        allCodes = [];
    }

    // Others = all codes not in the popular list
    const othersSet = new Set(allCodes.filter(c => !POPULAR_CURRENCIES.includes(c)));
    const othersSorted = [...othersSet].sort();

    // Full ordered list: popular + rest
    const fullList = [...POPULAR_CURRENCIES, ...othersSorted];

    // Build currency objects for rendering
    const currencies = fullList.map(code => ({
        code,
        name: getCurrencyName(code, displayNames),
        symbol: getCurrencySymbol(code)
    }));

    // ---- Build the overlay ----
    const overlay = document.createElement('div');
    overlay.className = 'bottom-sheet-overlay currency-picker-overlay';

    const sheet = document.createElement('div');
    sheet.className = 'bottom-sheet-content currency-picker-sheet';

    // ---- Handle bar ----
    const handle = document.createElement('div');
    handle.className = 'sheet-handle-bar';
    sheet.appendChild(handle);

    // ---- Title ----
    const titleEl = document.createElement('div');
    titleEl.className = 'currency-picker-title';
    titleEl.textContent = 'Select Currency';
    sheet.appendChild(titleEl);

    // ---- Search input ----
    const searchWrapper = document.createElement('div');
    searchWrapper.className = 'currency-picker-search-wrapper';

    const searchIcon = document.createElement('span');
    searchIcon.className = 'currency-search-icon';
    searchIcon.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
    `;

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = 'Search currencies…';
    searchInput.className = 'currency-picker-search';
    searchInput.setAttribute('autocomplete', 'off');
    searchInput.setAttribute('autocorrect', 'off');
    searchInput.setAttribute('spellcheck', 'false');
    searchInput.setAttribute('data-form-type', 'other');

    searchWrapper.appendChild(searchIcon);
    searchWrapper.appendChild(searchInput);
    sheet.appendChild(searchWrapper);

    // ---- Currency list ----
    const listEl = document.createElement('div');
    listEl.className = 'currency-picker-list';

    function renderList(filter) {
        listEl.innerHTML = '';
        const q = filter.trim().toLowerCase();

        const filtered = q
            ? currencies.filter(c =>
                c.code.toLowerCase().includes(q) ||
                c.name.toLowerCase().includes(q) ||
                c.symbol.toLowerCase().includes(q)
            )
            : currencies;

        if (filtered.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'currency-picker-empty';
            empty.textContent = 'No currencies found';
            listEl.appendChild(empty);
            return;
        }

        // Section header for popular currencies (only when not searching)
        let shownPopularHeader = false;
        let shownOtherHeader = false;

        filtered.forEach(c => {
            const isPopular = POPULAR_CURRENCIES.includes(c.code);
            const isSelected = c.code === currentCurrency;

            if (!q) {
                if (isPopular && !shownPopularHeader) {
                    const header = document.createElement('div');
                    header.className = 'currency-section-header';
                    header.textContent = 'Popular';
                    listEl.appendChild(header);
                    shownPopularHeader = true;
                } else if (!isPopular && !shownOtherHeader) {
                    const header = document.createElement('div');
                    header.className = 'currency-section-header';
                    header.textContent = 'All Currencies';
                    listEl.appendChild(header);
                    shownOtherHeader = true;
                }
            }

            const row = document.createElement('div');
            row.className = 'currency-picker-row' + (isSelected ? ' selected' : '');

            row.innerHTML = `
                <span class="currency-row-symbol">${c.symbol}</span>
                <span class="currency-row-info">
                    <span class="currency-row-code">${c.code}</span>
                    <span class="currency-row-name">${c.name}</span>
                </span>
                ${isSelected ? `<span class="currency-row-check">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                </span>` : ''}
            `;

            row.addEventListener('click', () => {
                closeSheet();
                onSelect(c.code);
            });
            listEl.appendChild(row);
        });
    }

    renderList('');
    sheet.appendChild(listEl);
    overlay.appendChild(sheet);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // ---- Search input live filter ----
    searchInput.addEventListener('input', () => renderList(searchInput.value));

    // ---- Animation ----
    requestAnimationFrame(() => overlay.classList.add('show'));

    // ---- Close logic ----
    const closeSheet = () => {
        document.body.style.overflow = '';
        overlay.classList.remove('show');
        sheet.style.transform = 'translateX(-50%) translateY(100%)';
        setTimeout(() => {
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        }, 300);
    };

    overlay.addEventListener('click', e => {
        if (e.target === overlay) closeSheet();
    });

    // ---- Drag to dismiss ----
    let startY = 0, currentY = 0, isDragging = false;

    sheet.addEventListener('touchstart', e => {
        // Allow drag only from the handle / title area
        if (!e.target.closest('.sheet-handle-bar') &&
            !e.target.closest('.currency-picker-title')) return;
        startY = e.touches[0].clientY;
        isDragging = true;
        sheet.style.transition = 'none';
    }, { passive: true });

    sheet.addEventListener('touchmove', e => {
        if (!isDragging) return;
        const delta = e.touches[0].clientY - startY;
        if (delta > 0) {
            if (e.cancelable) e.preventDefault();
            currentY = delta;
            sheet.style.transform = `translateX(-50%) translateY(${delta}px)`;
        }
    }, { passive: false });

    sheet.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        sheet.style.transition = 'transform 0.3s cubic-bezier(0.1,0.7,0.1,1)';
        if (currentY > 120) {
            closeSheet();
        } else {
            sheet.style.transform = 'translateX(-50%) translateY(0)';
            currentY = 0;
        }
    });

    // ---- Focus search after animation (desktop only) ----
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
        setTimeout(() => searchInput.focus(), 350);
    }
}
